import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getJobById } from "../../services/jobsService";
import '../../css/jobsdetail.css';
import { useAuth } from '../../context/AuthContext';
import { createLead } from '../../services/leadsService';
import { useSelector } from 'react-redux';
import { editUserProfile } from '../../services/userService';
import { useServiceRequestSearch } from '../../hooks/useServiceRequestSearch';
import { toast } from "react-toastify";
import { getImage } from '../../utils/cloudinarySizing';

const ServiceRequestDetailPage = () => {
  const { userId, role } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [interestSent, setInterestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestForm, setInterestForm] = useState({ name: "", email: "", phone: "" });
  const [activeImage, setActiveImage] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});

  const profile = useSelector((state) => state.profile.data);

  const { formatBudget } = useServiceRequestSearch(10);

  const isValidImage = (src) => {
    return typeof src === "string" && src.trim().length > 0;
  };
  useEffect(() => {
    const fetchJob = async () => {
      try {
        const data = await getJobById(id);
        setJob(data);
        setInterestSent(data.has_lead);
      } catch (err) {
        console.error("Error loading job:", err);
      }
    };
    fetchJob();
  }, [id]);

  useEffect(() => {
    if (showInterestModal && profile) {
      setInterestForm({
        name:
          role === "business" && profile.business_profile
            ? profile.business_profile.business_name
            : `${profile.first_name} ${profile.last_name}`.trim(),
        email: profile.profile.contact_email || "",
        phone: formatPhone(profile.profile.phone_number || ""),
      });
    }
  }, [showInterestModal, profile, role]);

  const formatPhone = (value) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "").substring(0, 10);
    const part1 = digits.substring(0, 3);
    const part2 = digits.substring(3, 6);
    const part3 = digits.substring(6, 10);

    if (digits.length > 6) return `(${part1}) ${part2}-${part3}`;
    if (digits.length > 3) return `(${part1}) ${part2}`;
    if (digits.length > 0) return `(${part1}`;
    return "";
  };

  const handleSendInterest = async () => {
    setLoading(true);
    try {
      await createLead(job.id);
      setInterestSent(true);
    } catch (err) {
      console.error("Failed to send interest:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInterestSubmit = async () => {
    if (!interestForm.email && !interestForm.phone) {
      alert("Please provide at least an email or phone number.");
      return;
    }

    // Check if anything changed compared to the profile
    const emailChanged = interestForm.email !== (profile.contact_email || profile.email);
    const phoneChanged =
      interestForm.phone.replace(/\D/g, "") !== (profile.profile.phone_number || "");

    if (emailChanged || phoneChanged) {
      try {
        const formData = new FormData();
        if (emailChanged) formData.append("contact_email", interestForm.email);
        if (phoneChanged) formData.append(
          "phone_number",
          interestForm.phone.replace(/\D/g, "")
        );

        await editUserProfile(formData);
        console.log("Profile updated successfully");
      } catch (err) {
        console.error("Failed to update profile:", err);
      }
    }

    await handleSendInterest();

    toast.success("Interest sent successfully!");
    setShowInterestModal(false);
  };

  const formatLocation = (job) => {
    if (!job) return "Not Available";
    const parts = [];
    if (job.city) parts.push(job.city);
    if (job.state) parts.push(job.state);
    if (job.country && job.country !== "US") parts.push(job.country);
    return parts.length > 0 ? parts.join(", ") : "Not Available";
  };

  const formattedDate = job
    ? new Date(job.created_at).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "";

  const formattedUpdatedDate = job
    ? new Date(job.updated_at).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : null;

  const canSendInterest = role === "freelancer" || role === "business";

  if (!job) return null;

  
  

  const profileImageSrc =
    !brokenImages[`user-${job.user_id}`] && job.profile?.photo_original
      ? getImage(job.profile.photo_original, "avatar_sm")
      : "/servspace-default-150x150.png";



  return (
    <div className="job-detail-wrapper">
      <div className="job-detail-content">
        <div className="job-detail-header-section">
          <div className="job-title-row">
            {/* Mobile image: above title & button */}
            <img
              src={profileImageSrc}
              alt="User profile"
              className="poster-profile-photo mobile-only"
              onClick={() => navigate(`/profile/${job.user_id}`)}
              onError={() =>
                setBrokenImages(prev => ({
                  ...prev,
                  [`user-${job.user_id}`]: true
                }))
              }
              style={{ cursor: "pointer" }}
            />

            <div className="title-and-button">
              <h2 className="job-detail-title">{job.title}</h2>

              {canSendInterest && (
                <button
                  className={`lead-interest-btn ${interestSent ? "sent" : ""}`}
                  onClick={() => setShowInterestModal(true)}
                  disabled={interestSent || loading}
                >
                  {interestSent ? (
                    <>
                      <span className="checkmark">✓</span> Interest Sent
                    </>
                  ) : loading ? (
                    "Sending..."
                  ) : (
                    "Send Interest"
                  )}
                </button>
              )}
            </div>

            <p className="jobs-page-info">
              <strong>Pay:</strong>{" "}
              <i className="fas fa-dollar-sign money-icon"></i> {formatBudget(job)} / {job.budget_type}
              <span className="separator">|</span>
              <i className="fas fa-location-dot location-icon"></i>{" "}
              <strong>Location:</strong> {formatLocation(job)}

              {/* Desktop image: under Pay/Location */}
              <img
                src={profileImageSrc}
                alt="User profile"
                className="poster-profile-photo desktop-only"
                onClick={() => navigate(`/profile/${job.user_id}`)}
                onError={(e) => {
                  e.target.onerror = null;

                  setBrokenImages(prev => ({
                    ...prev,
                    [`user-${job.user_id}`]: true
                  }));
                }}
                style={{ cursor: "pointer" }}
              />
            </p>
          </div>
        </div>

        {job.images?.length > 0 && (
          <div className="job-images-scroll">
            <div className="job-images-row">
              {job.images.map((imgObj, idx) => {
                const imageSrc = imgObj?.image;

                if (!isValidImage(imageSrc)) return null;

                return (
                  <img
                    key={idx}
                    src={getImage(imageSrc, "srv_med")}
                    alt={`Job image ${idx + 1}`}
                    className="job-image"
                    onClick={() => setActiveImage(imageSrc)}
                    onError={(e) => {
                      e.currentTarget.remove();
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        <div className="job-detail-section">
          <p>{job.description}</p>
        </div>

        <div className="job-date-row">
          <div className="job-date-bubble">
            <p className="job-created-at">Posted on {formattedDate}</p>
          </div>

          {job.updated_at && (
            <div className="job-date-bubble">
              <p className="job-updated-at">Updated on {formattedUpdatedDate}</p>
            </div>
          )}
        </div>
      </div>

      {activeImage && (
        <div className="image-overlay" onClick={() => setActiveImage(null)}>
          <img
            src={activeImage}
            alt="Full size"
            className="image-overlay-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {showInterestModal && (
        <div className="interest-modal-overlay">
          <div className="interest-modal">
            <h3>Send Interest</h3>

            <div className="interest-card">
              <div className="interest-left">
                <img
                  src={
                    profile?.profile?.photo_150_url ||
                    (role === "freelancer"
                      ? "/servspace-placeholder-500x500.png"
                      : role === "business"
                        ? "/servspace-business-placeholder.png"
                        : "/servspace-default-150x150.png")
                  }
                  alt="Profile"
                  className="interest-profile-photo"
                />
                <span className="interest-name">
                  {role === "business" && profile.business_profile
                    ? profile.business_profile.business_name
                    : role === "freelancer" && profile.freelancer_profile
                      ? `${profile.first_name || profile.user?.first_name || ""} ${profile.last_name || profile.user?.last_name || ""}`.trim()
                      : "Not Available"}
                </span>
              </div>

              <div className="interest-right">
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={interestForm.email}
                  onChange={(e) =>
                    setInterestForm({ ...interestForm, email: e.target.value })
                  }
                />

                <input
                  type="text"
                  value={interestForm.phone}
                  onChange={(e) =>
                    setInterestForm({ ...interestForm, phone: formatPhone(e.target.value) })
                  }
                  placeholder="Phone (optional)"
                />
              </div>
            </div>

            <div className="interest-buttons-row">
              <button className="interest-submit-btn" onClick={handleInterestSubmit}>
                Send Interest
              </button>
              <button className="interest-cancel-btn" onClick={() => setShowInterestModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestDetailPage;